import { useEffect } from "react";
import { CheckCircle, Award, Users, Target } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatsSection from "@/components/StatsSection";
import CTASection from "@/components/CTASection";
import MrMatthias from '@/assets/Mr. Matthias M. Ndeulita.png';

export default function About() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Executive Summary */}
      <section className="bg-gradient-to-r from-seafarer-orange/10 to-seafarer-purple/10 py-10 px-4 md:px-0">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-lg p-8 border-l-4 border-seafarer-orange">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-seafarer-orange">Executive Summary</h2>
          <p className="mb-4 text-gray-800 dark:text-gray-200">
            The October Seafarers United Land Housing Federation is an inclusive Land & Housing Platform; that enables fishermen to share ideas, and innovative approaches with a capitalist element on saving and investment, while establishing sustainable business ecosystems that promotes an entrepreneurship culture amongst fisherman, in order to reduce concentrated poverty and racial wealth gap within harbor towns.
          </p>
          <p className="mb-4 text-gray-800 dark:text-gray-200">
            With an active membership of over 420 members, the federation has managed to raise through monthly contribution approximately N$ 880,000.00 as of 2021, and prospects the amount might double within (2) years. Whereas our first priority is to purchase land and develop at least 150 houses in the first phase of the Project, on a land mass that covers at least 48,2575 hectors, at Portion 18 of Farm 37.
          </p>
          <p className="mb-4 text-gray-800 dark:text-gray-200">
            As part of our strategic plan, we intend to improve the quality of environment at farm 37 while forming architectural beauty factors that will increase income and improve the welfare of fishermen and their families, by creating harmony and balance on the natural ecology; while arranging green open spaces; that are needed as aesthetic elements, entailing the importance of flora and fauna in ecological functions, including the prevalence of oxygen, shade, absorption of pollutants in the air, water & soil, and acting as wind barriers.
          </p>
          <p className="mb-4 text-gray-800 dark:text-gray-200">
            Since its common knowledge that civilization is an essential equilibrium of precision, we are keen and eager to playing a pivotal role in ameliorating the lives of fisherman in particular, and bestow confidence in employees that work in the fishing industry; that in the 21st century everyone deserves a decent house built of quality materials, and entail the profoundness of civilization.
          </p>
          <div className="mt-8">
            <p className="italic text-gray-700 dark:text-gray-300 mb-2">Yours Sincerely,</p>
            <p className="font-bold text-gray-900 dark:text-white">Mr. Matthias M. Ndeulita,</p>
            <p className="text-gray-700 dark:text-gray-300">Executive Chairman</p>
          </div>
        </div>
      </section>
      
      {/* About Us Hero Section */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-seafarer-orange">About Us</h1>
            <p className="text-lg text-gray-700 dark:text-gray-200">
              Learn more about the October Seafarers United Land & Housing Federation and our mission to improve the lives of fishermen and their families.
            </p>
          </div>
        </div>
      </section>
      
      {/* Our Story Section */}
      <section className="section-padding">
        <div className="page-container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-seafarer-purple">Our Story</h2>
            <p className="mb-4 text-gray-800 dark:text-gray-200">
              The October Seafarers United Land and Housing Federation is a dynamic saving and investment scheme that was formed in 2021 at Walvis Bay by Mr. Matthias M. Ndeulita, with an aim of targeting challenges and providing tangible solutions to housing shortages; through the purchase and development of land in proclaim and unproclaim areas.
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-200">
              As of 2021, the federation has made tremendous achievements and progress on the prospects of acquiring and purchasing Portion 18 - off Farm 37. After a conditional approval and reservation was granted by the Municipality of Walvis Bay Ordinary Council Meeting, to develop a fisherman village, the federation is further expanding to coastal towns like in Swakopmund, Henties bay, and Luderitze, respectifully, that will carter for low income earners working in the Fishing Industry.
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-200">
              Through our strategic endeavor, the federation is determined to champion fishermen as equal partners in the fight against poverty, and play a pivotal role in the decongestion of Kuisebmund, that will help minimize fire hazards that are posed by lack of land & house ownership, and mushrooming of informal structures that are built with combustible materials.
            </p>
            <ul className="mb-4 text-gray-800 dark:text-gray-200 list-disc pl-6 inline-block text-left">
              <li>Land Development has started in 2024 to 2025.</li>
              <li>We are looking at building 200 houses in phase one in 2025 to 2026.</li>
              <li>We have currently 588 active members of the organization.</li>
            </ul>
            <div className="mt-6">
              <a
                href="/documents/Preliminary Plan_28-02-2025_250301_193054.pdf"
                download
                className="inline-flex items-center px-6 py-3 bg-seafarer-orange text-white font-semibold rounded-lg shadow hover:bg-orange-600 transition-colors mb-8 mt-4"
              >
                Download Preliminary Plan PDF
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission & Vision */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission & Vision</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="seafarer-card p-8 hover:shadow-lg transition-all duration-300 animate-fade-in">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-seafarer-orange/10 mb-6">
                <Target className="h-8 w-8 text-seafarer-orange" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Our mission is to establish a model framework that bolsters the National
                Development Plans (NDP's), and create a follow-up mechanism that supports
                Namibian Fisherman as equal partners in the fight against poverty.
              </p>
            </div>
            
            <div className="seafarer-card p-8 hover:shadow-lg transition-all duration-300 animate-fade-in [animation-delay:200ms]">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-seafarer-purple/10 mb-6">
                <Award className="h-8 w-8 text-seafarer-purple" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-gray-600 dark:text-gray-300">
                The Federation strives to build at least 1200 Houses within the period of 6-10 years.
                And generate exceptional talent, and expand investment opportunities and
                initiatives to improve the welfare of fishermen and their families.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="section-padding">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Our Values</h2>
            <p className="text-lg text-white">
              These core values guide our actions and decisions every day.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Commitment",
                description: "We are bound emotionally and intellectually to every situation."
              },
              {
                title: "Collaboration",
                description: "We promote effective partnership & cooperation."
              },
              {
                title: "Resiliency",
                description: "To respond to changing markets, technology and environmental forces."
              },
              {
                title: "Diversity",
                description: "Provide for diverse and affordable housing, meeting the needs of all residents."
              },
              {
                title: "Integrity",
                description: "We stand up for what is morally right, and fulfill commitments and promises."
              },
              {
                title: "Excellence",
                description: "To become superior and impeccable in our services, performance and output."
              },
            ].map((value, index) => (
              <div 
                key={value.title} 
                className="seafarer-card p-6 hover:shadow-lg transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center mb-4">
                  <div className="mr-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-seafarer-orange to-seafarer-purple text-white">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold">{value.title}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Leadership</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Meet the dedicated team behind the October Seafarers United Land & Housing Federation.
            </p>
          </div>
          
          {/* Top Leadership */}
          <h3 className="text-2xl font-bold mb-6 text-seafarer-orange">Top Leadership</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="seafarer-card overflow-hidden animate-fade-in">
              <div className="relative h-64 w-full">
                <img src="/Mr. Matthias M. Ndeulita Executive Chairman & President.png" alt="Mr. Matthias M. Ndeulita" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1 drop-shadow-lg opacity-100">Mr. Matthias M. Ndeulita</h3>
                <p className="text-seafarer-purple mb-4 drop-shadow-lg opacity-100">Executive Chairman & President</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:100ms]">
              <div className="relative h-64 w-full">
                <img src="/Mr. Moses Hitilasha Deput Chairman.png" alt="Mr. Moses Hitilasha" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Moses Hitilasha</h3>
                <p className="text-seafarer-purple mb-4">Deputy Chairman</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:200ms]">
              <div className="relative h-64 w-full">
                <img src="/Mr Wilhem Lotokeni Secretary General.png" alt="Mr. Wilhem Lotokeni" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Wilhem Lotokeni</h3>
                <p className="text-seafarer-purple mb-4">Secretary General</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:300ms]">
              <div className="relative h-64 w-full">
                <img src="/Mr. Johannes Nefungo Dep Secretary General.png" alt="Mr. Johannes Nefungo" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Johannes Nefungo</h3>
                <p className="text-seafarer-purple mb-4">Deputy Secretary General</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:400ms]">
              <div className="relative h-64 w-full">
                <img src="/Mr. Theofelus Shilimela Treasure & Legal Secretary.png" alt="Mr. Theofelus Shilimela" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Theofelus Shilimela</h3>
                <p className="text-seafarer-purple mb-4">Treasurer & Legal Secretary</p>
              </div>
            </div>
          </div>

          {/* Directors */}
          <h3 className="text-2xl font-bold mb-6 text-seafarer-orange">Directors</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="seafarer-card overflow-hidden animate-fade-in">
              <div className="relative h-64 w-full">
                <img src="/Mr. Joseph Garoeb Director of Strategic Investments & Corporate Affairs.png" alt="Mr. Joseph Garoeb" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Joseph Garoeb</h3>
                <p className="text-seafarer-purple mb-4">Director of Strategic Investments & Corporate Affairs</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:100ms]">
              <div className="relative h-64 w-full">
                <img src="/Ms Tuhafeni Nashivela Director of Finance, Economic Developmend & Strategic Assets.png" alt="Ms. Tuhafeni Nashivela" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Ms. Tuhafeni Nashivela</h3>
                <p className="text-seafarer-purple mb-4">Director of Finance, Economic Development & Strategic Assets</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:200ms]">
              <div className="relative h-64 w-full">
                <img src="/Mr. David Binda Director of Sports, Entertainment & Social Development.png" alt="Mr. David Binda" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. David Binda</h3>
                <p className="text-seafarer-purple mb-4">Director of Sports, Entertainment & Social Development</p>
              </div>
            </div>
          </div>

          {/* Officers & Admins */}
          <h3 className="text-2xl font-bold mb-6 text-seafarer-orange">Officers & Admins</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="seafarer-card overflow-hidden animate-fade-in">
              <div className="relative h-64 w-full">
                <img src="/Mr Andrew Hilunanje IT, Marketing & Communication Officer.png" alt="Mr. Andrew Hilunanje" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Andrew Hilunanje</h3>
                <p className="text-seafarer-purple mb-4">IT, Marketing & Communication Officer</p>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:100ms]">
              <div className="relative h-64 w-full">
                <img src="/Ms Michele Wendy Admin, Chief Regional.png" alt="Ms. Michele Wendy" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Ms. Michele Wendy</h3>
                <p className="text-seafarer-purple mb-4">Admin, Chief Regional</p>
              </div>
            </div>
          </div>

          {/* Committee Members */}
          <h3 className="text-2xl font-bold mb-6 text-seafarer-orange">Committee Members</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="seafarer-card overflow-hidden animate-fade-in">
              <div className="relative h-64 w-full">
                <img src="/Mr Ananias Shitilu.png" alt="Mr. Ananias Shitilu" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Ananias Shitilu</h3>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:100ms]">
              <div className="relative h-64 w-full">
                <img src="/Mr Tobias Nangolo.png" alt="Mr. Tobias Nangolo" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Mr. Tobias Nangolo</h3>
              </div>
            </div>
            <div className="seafarer-card overflow-hidden animate-fade-in [animation-delay:200ms]">
              <div className="relative h-64 w-full">
                <img src="/Ms Ndeshi Nepaya.png" alt="Ms. Ndeshi Nepaya" className="h-64 w-full object-cover object-center bg-gray-200 dark:bg-gray-700" />
                <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1">Ms. Ndeshi Nepaya</h3>
              </div>
            </div>
          </div>

          {/* Group Photo */}
          <div className="mt-12 flex justify-center">
            <img src="/team.png" alt="Team Group Photo" className="rounded-lg shadow-xl max-w-4xl w-full" />
          </div>
        </div>
      </section>
      
      <StatsSection />
      <CTASection />
      
      <Footer />
    </div>
  );
}

